import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { codeRepoAPI } from '../services/api';
import { Skeleton } from '@mui/material';
import { MenuBook, CheckCircle, HourglassEmpty, FlagOutlined, BarChart } from '@mui/icons-material';

const MyCodeRepos = () => {
  const navigate = useNavigate();
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRepos, setFilteredRepos] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0
  });

  useEffect(() => {
    fetchMyRepos();
  }, []);

  useEffect(() => {
    filterRepos();
  }, [repos, searchTerm]);

  const fetchMyRepos = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getMyRepos();
      setRepos(response.data.repos || []);
      setStatistics(response.data.statistics || {
        total: 0,
        completed: 0,
        inProgress: 0,
        notStarted: 0
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching code repos');
      console.error('Error fetching my repos:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterRepos = () => {
    if (!searchTerm) {
      setFilteredRepos(repos);
      return;
    }

    const filtered = repos.filter(repo =>
      repo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      repo.repoCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      repo.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRepos(filtered);
  };

  const handleViewDetails = (repoCode) => {
    navigate(`/my-code-repos/${repoCode}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const isRepoActive = (repo) => {
    if (!repo.startDate && !repo.endDate) return true;
    const now = new Date();
    const start = repo.startDate ? new Date(repo.startDate) : null;
    const end = repo.endDate ? new Date(repo.endDate) : null;

    if (start && now < start) return false; // Not started yet
    if (end && now > end) return false; // Already ended
    return true;
  };

  return (
    <div className="container" style={{ backgroundColor: 'white', minHeight: '100vh', borderRadius: '12px' }}>
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/user')}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to Dashboard
          </button>
          <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
            📦 My Code Repositories
          </h2>
          <p style={{ color: '#718096', fontSize: '13px' }}>
            Browse coding assignment collections assigned to you
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Statistics Dashboard */}
        {loading ? (
          <>
            <div style={{
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <div style={{
                width: '240px',
                height: '24px',
                backgroundColor: '#e2e8f0',
                borderRadius: '6px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{
                  borderRadius: '10px',
                  padding: '12px',
                  backgroundColor: '#e2e8f0',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.1}s`,
                  height: '120px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    backgroundColor: '#cbd5e0',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }} />
                  <div style={{
                    width: '60px',
                    height: '32px',
                    backgroundColor: '#cbd5e0',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }} />
                  <div style={{
                    width: '80px',
                    height: '12px',
                    backgroundColor: '#cbd5e0',
                    borderRadius: '4px',
                    marginBottom: '8px'
                  }} />
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#cbd5e0',
                    borderRadius: '2px'
                  }} />
                </div>
              ))}
            </div>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.6; }
              }
            `}</style>
          </>
        ) : statistics.total > 0 ? (
          <>
            <div style={{
              marginBottom: '12px',
              paddingBottom: '10px',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '700',
                color: '#2d3748',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <BarChart sx={{ fontSize: '18px' }} /> Your Progress Overview
              </h3>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {/* Total Assignments */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                padding: '12px',
                color: 'white',
                boxShadow: '0 10px 25px -5px rgba(102, 126, 234, 0.3), 0 8px 10px -6px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px -5px rgba(102, 126, 234, 0.4), 0 10px 20px -6px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(102, 126, 234, 0.3), 0 8px 10px -6px rgba(102, 126, 234, 0.3)';
              }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  marginBottom: '6px'
                }}>
                  <MenuBook sx={{ fontSize: '28px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                </div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '800',
                  marginBottom: '4px',
                  letterSpacing: '-0.5px'
                }}>
                  {statistics.total}
                </div>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.95,
                  fontWeight: '600',
                  letterSpacing: '0.2px'
                }}>
                  Total Assignments
                </div>
              </div>

              {/* Completed */}
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '10px',
                padding: '12px',
                color: 'white',
                boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.3), 0 8px 10px -6px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px -5px rgba(16, 185, 129, 0.4), 0 10px 20px -6px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(16, 185, 129, 0.3), 0 8px 10px -6px rgba(16, 185, 129, 0.3)';
              }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  marginBottom: '6px'
                }}>
                  <CheckCircle sx={{ fontSize: '28px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    letterSpacing: '-0.5px'
                  }}>
                    {statistics.completed}
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    opacity: 0.9,
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '6px'
                  }}>
                    {statistics.total > 0 ? Math.round((statistics.completed / statistics.total) * 100) : 0}%
                  </div>
                </div>
                <div style={{
                  fontSize: '12px',
                  opacity: 0.95,
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  marginBottom: '6px'
                }}>
                  Completed
                </div>
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${statistics.total > 0 ? (statistics.completed / statistics.total) * 100 : 0}%`,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '2px',
                    transition: 'width 0.6s ease-out'
                  }} />
                </div>
              </div>

              {/* In Progress */}
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: '10px',
                padding: '12px',
                color: 'white',
                boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.3), 0 8px 10px -6px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px -5px rgba(245, 158, 11, 0.4), 0 10px 20px -6px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(245, 158, 11, 0.3), 0 8px 10px -6px rgba(245, 158, 11, 0.3)';
              }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  marginBottom: '6px'
                }}>
                  <HourglassEmpty sx={{ fontSize: '28px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    letterSpacing: '-0.5px'
                  }}>
                    {statistics.inProgress}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    opacity: 0.9,
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '2px 6px',
                    borderRadius: '4px'
                  }}>
                    {statistics.total > 0 ? Math.round((statistics.inProgress / statistics.total) * 100) : 0}%
                  </div>
                </div>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.95,
                  fontWeight: '600',
                  letterSpacing: '0.2px',
                  marginBottom: '4px'
                }}>
                  In Progress
                </div>
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${statistics.total > 0 ? (statistics.inProgress / statistics.total) * 100 : 0}%`,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '2px',
                    transition: 'width 0.6s ease-out'
                  }} />
                </div>
              </div>

              {/* Not Started */}
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '10px',
                padding: '12px',
                color: 'white',
                boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.3), 0 8px 10px -6px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px -5px rgba(139, 92, 246, 0.4), 0 10px 20px -6px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(139, 92, 246, 0.3), 0 8px 10px -6px rgba(139, 92, 246, 0.3)';
              }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  right: '-15px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  marginBottom: '6px'
                }}>
                  <FlagOutlined sx={{ fontSize: '28px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    letterSpacing: '-0.5px'
                  }}>
                    {statistics.notStarted}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    opacity: 0.9,
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '2px 6px',
                    borderRadius: '4px'
                  }}>
                    {statistics.total > 0 ? Math.round((statistics.notStarted / statistics.total) * 100) : 0}%
                  </div>
                </div>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.95,
                  fontWeight: '600',
                  letterSpacing: '0.2px',
                  marginBottom: '4px'
                }}>
                  To Be Started
                </div>
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${statistics.total > 0 ? (statistics.notStarted / statistics.total) * 100 : 0}%`,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '2px',
                    transition: 'width 0.6s ease-out'
                  }} />
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Search */}
        <div style={{
          backgroundColor: '#f7fafc',
          border: '1.5px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '0'
        }}>
          <h3 style={{ color: '#2d3748', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            🔍 Search Repositories
          </h3>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            Find repositories by title, code, or description
          </p>
          <input
            type="text"
            placeholder="Type to search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
      </div>

      {loading ? (
        <div>
          {/* Repository cards skeleton */}
          <div style={{ display: 'grid', gap: '12px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '18px',
                  background: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    {/* Title */}
                    <Skeleton variant="text" width="60%" height={24} sx={{ marginBottom: '8px' }} />
                    
                    {/* Description */}
                    <Skeleton variant="text" width="90%" height={18} sx={{ marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={18} sx={{ marginBottom: '12px' }} />
                    
                    {/* Badges */}
                    <div style={{ marginBottom: '12px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
                      <Skeleton variant="rectangular" width={90} height={20} sx={{ borderRadius: '10px' }} />
                    </div>
                    
                    {/* Category tags */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <Skeleton variant="rectangular" width={60} height={22} sx={{ borderRadius: '11px' }} />
                      <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '11px' }} />
                      <Skeleton variant="rectangular" width={80} height={22} sx={{ borderRadius: '11px' }} />
                    </div>
                  </div>
                  
                  {/* Status badge */}
                  <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: '6px' }} />
                </div>
                
                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={16} sx={{ margin: '0 auto' }} />
                  </div>
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={16} sx={{ margin: '0 auto' }} />
                  </div>
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={16} sx={{ margin: '0 auto' }} />
                  </div>
                </div>
                
                {/* Action button */}
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '6px' }} />
              </div>
            ))}
          </div>
        </div>
      ) : filteredRepos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
            {repos.length === 0 ? 'No code repositories assigned yet' : 'No matching repositories found'}
          </p>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            {repos.length === 0
              ? 'Check back later or contact your instructor!'
              : 'Try adjusting your search terms'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {filteredRepos.map(repo => {
            const active = isRepoActive(repo);
            return (
              <div
                key={repo._id}
                onClick={() => handleViewDetails(repo.repoCode)}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '18px',
                  background: '#fafafa',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  opacity: active ? 1 : 0.7
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      color: '#2d3748', 
                      fontSize: '16px', 
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      {repo.title}
                    </h3>

                    {repo.description && (
                      <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                        {repo.description.length > 120 
                          ? repo.description.substring(0, 120) + '...' 
                          : repo.description}
                      </p>
                    )}

                    {/* Tags/Badges */}
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 8px',
                        background: 'white',
                        color: '#667eea',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: '1px solid #c7d2fe'
                      }}>
                        📋 {repo.repoCode}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: 'white',
                        color: getLevelColor(repo.level),
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: `1px solid ${getLevelColor(repo.level)}40`
                      }}>
                        {repo.level}
                      </span>
                      {!active && (
                        <span style={{
                          padding: '2px 8px',
                          background: '#fef2f2',
                          color: '#991b1b',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #fca5a5'
                        }}>
                          ⚠️ Inactive
                        </span>
                      )}
                      {repo.category && (
                        <span style={{
                          padding: '2px 8px',
                          background: '#f0fff4',
                          color: '#2f855a',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #9ae6b4'
                        }}>
                          📂 {repo.category.name}
                        </span>
                      )}
                      {repo.tags && repo.tags.length > 0 && repo.tags.slice(0, 2).map(tag => (
                        <span
                          key={tag._id}
                          style={{
                            padding: '2px 8px',
                            background: '#f0fff4',
                            color: '#2f855a',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '500',
                            border: '1px solid #9ae6b4'
                          }}
                        >
                          🏷️ {tag.name}
                        </span>
                      ))}
                      {repo.tags && repo.tags.length > 2 && (
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: 'white',
                          color: '#718096',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500'
                        }}>
                          +{repo.tags.length - 2}
                        </span>
                      )}
                    </div>
                    
                    <p style={{ color: '#4a5568', fontSize: '12px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: 'white',
                        color: '#4a5568',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: '1px solid #e2e8f0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        📝 <strong>{repo.assignmentCount || repo.assignments?.length || 0}</strong> Assignment{(repo.assignmentCount || repo.assignments?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      {(repo.startDate || repo.endDate) && (
                        <span style={{ color: '#718096' }}>
                          🗓️ {repo.startDate && formatDate(repo.startDate)}
                          {repo.startDate && repo.endDate && ' - '}
                          {repo.endDate && formatDate(repo.endDate)}
                        </span>
                      )}
                    </p>
                  </div>
                  
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: active ? '#dbeafe' : '#fef2f2',
                    color: active ? '#1e40af' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    marginLeft: '12px'
                  }}>
                    {active ? '✓ Active' : '⚠️ Inactive'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyCodeRepos;
